import { useStore } from '@nanostores/react';
import type { UIMessage } from 'ai';
import { DefaultChatTransport, generateId } from 'ai';
import { useChat } from '@ai-sdk/react';
import { useAnimate } from 'framer-motion';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useMessageParser, usePromptEnhancer, useShortcuts } from '~/lib/hooks';
import {
  chatId,
  chatMetadata,
  description as descriptionAtom,
  deriveOptimisticChatDescriptionFromText,
  ensureChatId,
  navigateChat,
  useChatHistory,
  getDraft,
  setDraft,
  clearDraft,
  pruneDrafts,
  setMessages as saveChatToServer,
} from '~/lib/persistence';
import { chatStore } from '~/lib/stores/chat';
import { workbenchStore } from '~/lib/stores/workbench';
import { runtime } from '~/lib/runtime';
import { DEFAULT_MODEL } from '~/utils/constants';
import { providerListStore, defaultProviderStore } from '~/lib/provider-list';
import { cubicEasingFn } from '~/utils/easings';
import { createScopedLogger, renderLogger } from '~/utils/logger';
import { BaseChat } from './BaseChat';
import { withAbortedMetadata } from './aborted-metadata';
import { latestTaskUpdate } from './task-update';
import { buildResumeContinuation } from './resume-prompt';
import { RESUME_BUILD_MARKER } from '~/utils/constants';
// Resume instructions live in a markdown file (hidden from the chat UI — the
// user sees a "Continue" chip). Vite inlines the file's text via `?raw`.
import continuePromptMd from './continue-prompt.md?raw';
import type { ProjectTabId } from './ChatBox';
import Cookies from 'js-cookie';
import { debounce } from '~/utils/debounce';
import { useSettings } from '~/lib/hooks/useSettings';
import type { ProviderInfo } from '~/types/model';
import { useParams, useNavigate } from 'react-router-dom';
import { createSampler } from '~/utils/sampler';
import { logStore } from '~/lib/stores/logs';
import { streamingState } from '~/lib/stores/streaming';
import { $activeGeneration } from '~/lib/stores/activeGeneration';
import { $activeProjectId } from '~/lib/stores/projects';
import { setLiveCredits } from '~/lib/stores/credits';
import { capture, captureException, captureRequestError, addExceptionStep } from '~/lib/posthog';
import { canReachServer, isNetworkError, whenReachable } from '~/lib/network-state';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { foldResumeBody, isResumeStreamUrl } from '~/lib/streaming/resume-fold';
import { $accessToken, $authLoading, $isAuthenticated, clearAuth } from '~/lib/stores/auth';
import { $turnQueue, enterQueue, leaveQueue, setQueueAdmitHandler } from '~/lib/stores/turn-queue';
import { apiRefreshToken } from '~/lib/api/auth';
import { ENCODED_JSON_CONTENT_TYPE, encodeJsonBody, isFirewallBlockPage } from '~/lib/encoded-json';
import { $showAuthModal, $pendingMessage, $pendingFiles } from '~/lib/stores/authModal';
import { hasLandingAutostart, clearLandingAutostart } from '~/lib/attribution';
import {
  $elementEditFallback,
  $selectedElement,
  $styleSaveRequest,
  clearSelection,
} from '~/lib/stores/element-selector';
import { $integrationRequest } from '~/lib/stores/integrations';
import { $restoreRequest, exitPeek } from '~/lib/stores/version-peek';
import {
  buildSelectedElementMarker,
  buildStyleEditPrompt,
  DELETE_ELEMENT_INSTRUCTION,
} from '~/lib/element-selector/markers';
import { trackSelectionSentToChat } from '~/lib/element-selector/analytics';
import { apiUploadStorageFile } from '~/lib/api/storage';
import {
  cancelGeneration,
  listCheckpoints,
  restoreCheckpoint,
  mapRestoreErrorCode,
  apiGetActiveGeneration,
} from '~/lib/api/chats';
import { isOurTurnStillRunning, type ActiveGenerationProbe } from './interrupted-stream';
import { isTransportFailure } from './transport-failure';
import { compressImageFile } from '~/lib/image/compress';
import { BrochureError, processBrochurePdf, type BrochureAttachment } from '~/lib/pdf/brochure';
import { uploadBrochureArtifacts } from '~/lib/pdf/upload-brochure';
import { extractPdfText, isPdfFile, type PdfDocAttachment } from '~/lib/pdf/pdf-text';
import type { DesignScheme } from '~/types/design-scheme';
import type { FileUIPart } from 'ai';

import type { LlmErrorAlertType } from '~/types/actions';
import { composerMaxHeight } from './composer-height';

const logger = createScopedLogger('Chat');

export function Chat() {
  renderLogger.trace('Chat');

  const { id } = useParams();
  const { ready, initialMessages, activeStreamId, storeMessageHistory, importChat, exportChat } = useChatHistory(id);
  const title = useStore(descriptionAtom);

  return (
    <>
      {ready ? (
        <ChatImpl
          description={title}
          initialMessages={initialMessages}
          shouldResume={!!activeStreamId}
          exportChat={exportChat}
          storeMessageHistory={storeMessageHistory}
          importChat={importChat}
        />
      ) : (
        <div className="ui-root flex items-center justify-center h-full w-full bg-page">
          <div className="flex flex-col items-center gap-4">
            <div className="i-svg-spinners:90-ring-with-bg text-4xl text-primary" />
            <p className="text-secondary text-[13px] font-geist">Loading chat…</p>
          </div>
        </div>
      )}
    </>
  );
}

const processSampledMessages = createSampler(
  (options: {
    messages: UIMessage[];
    initialMessages: UIMessage[];
    isLoading: boolean;
    parseMessages: (messages: UIMessage[], isLoading: boolean) => void;
  }) => {
    const { messages, isLoading, parseMessages } = options;
    parseMessages(messages, isLoading);
  },
  50,
);

interface ChatProps {
  initialMessages: UIMessage[];
  storeMessageHistory: (messages: UIMessage[]) => Promise<void>;
  importChat: (description: string, messages: UIMessage[]) => Promise<void>;
  exportChat: () => void;
  description?: string;
  // True when the loaded chat has an in-flight generation to reconnect to.
  shouldResume?: boolean;
}

export const ChatImpl = memo(
  ({ description, initialMessages, shouldResume, storeMessageHistory, importChat, exportChat }: ChatProps) => {
    useShortcuts();

    const navigate = useNavigate();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const sendMessageRef = useRef<((event: React.UIEvent, messageInput?: string) => void) | null>(null);
    // Canonical chat id (chats.id) used for resume reconnect; set once the chat loads.
    const resolvedChatId = useStore(chatId);
    /*
     * Tracks whether the user has initiated a send in this mount. Lets us treat
     * pre-send stream errors (i.e. resume-reconnect failures) as non-fatal and
     * fall back to the loaded messages instead of showing an error alert.
     */
    const hasUserSentRef = useRef(false);
    const [chatStarted, setChatStarted] = useState(initialMessages.length > 0);
    // A boot that failed on the hero (before chatStarted flips) must still show
    // the recoverable retry banner — chatStarted alone would gate it out and
    // leave a toast-only dead end. Component-local, so it can't leak a stale
    // banner onto a fresh hero (ChatImpl remounts per chat) (DEV-146).
    const [sandboxBootFailed, setSandboxBootFailed] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [imageDataList, setImageDataList] = useState<string[]>([]);
    const [addToProject, setAddToProject] = useState<boolean[]>([]);
    const [brochure, setBrochure] = useState<BrochureAttachment | null>(null);
    const [pdfDocs, setPdfDocs] = useState<PdfDocAttachment[]>([]);
    const [fakeLoading, setFakeLoading] = useState(false);
    const [isStartingChat, setIsStartingChat] = useState(false);
    // messageIds that have a restorable checkpoint — drives the revert button's visibility.
    const [checkpoints, setCheckpoints] = useState<Set<string>>(new Set());
    // True for the duration of a checkpoint restore — blocks sending a new
    // message and hides revert buttons so a concurrent pod mutation / whole-array
    // saveChat race can't clobber the restore card mid-flight.
    const [isRestoring, setIsRestoring] = useState(false);
    const files = useStore(workbenchStore.files);
    const [designScheme, setDesignScheme] = useState<DesignScheme | undefined>(undefined);
    const actionAlert = useStore(workbenchStore.alert);
    const { activeProviders } = useSettings();
    const providerList = useStore(providerListStore);
    const defaultProvider = useStore(defaultProviderStore);
    const [llmErrorAlert, setLlmErrorAlert] = useState<LlmErrorAlertType | undefined>(undefined);
    const [model, setModel] = useState(() => {
      const savedModel = Cookies.get('selectedModel');
      return savedModel || DEFAULT_MODEL;
    });
    const [provider, setProvider] = useState<ProviderInfo | undefined>(() => {
      const savedProvider = Cookies.get('selectedProvider');
      const list = providerListStore.get();
      const fallback = defaultProviderStore.get();

      return list.find((p) => p.name === savedProvider) || fallback;
    });

    // Update provider when store data loads asynchronously
    useEffect(() => {
      if (!provider && providerList.length > 0) {
        const savedProvider = Cookies.get('selectedProvider');
        setProvider(providerList.find((p) => p.name === savedProvider) || defaultProvider || providerList[0]);
      }
    }, [providerList, defaultProvider, provider]);

    // Style panel "Save edits" → one agent turn. The atom is nulled before
    // dispatch so this effect doesn't re-fire on its own write.
    const styleSaveRequest = useStore($styleSaveRequest);

    useEffect(() => {
      if (!styleSaveRequest) {
        return;
      }

      const prompt = buildStyleEditPrompt(styleSaveRequest.selection, styleSaveRequest.deltas);
      $styleSaveRequest.set(null);
      sendMessageRef.current?.(new Event('submit') as unknown as React.UIEvent, prompt);
    }, [styleSaveRequest]);

    // Direct edit (text/image/style) that couldn't be applied deterministically
    // → fall back to a normal agent turn with a ready-made prompt. Nulled
    // before dispatch so this effect doesn't re-fire on its own write.
    const elementEditFallback = useStore($elementEditFallback);

    useEffect(() => {
      if (!elementEditFallback) {
        return;
      }

      const prompt = elementEditFallback;
      $elementEditFallback.set(null);
      sendMessageRef.current?.(new Event('submit') as unknown as React.UIEvent, prompt);
    }, [elementEditFallback]);

    // Integrations panel "Add" tile → inject its prompt as a normal chat turn.
    // Nulled before dispatch so this effect doesn't re-fire on its own write.
    const integrationRequest = useStore($integrationRequest);

    useEffect(() => {
      if (!integrationRequest) {
        return;
      }

      const prompt = integrationRequest;
      $integrationRequest.set(null);
      sendMessageRef.current?.(new Event('submit') as unknown as React.UIEvent, prompt);
    }, [integrationRequest]);

    const { showChat } = useStore(chatStore);
    const [animationScope, animate] = useAnimate();
    const [chatMode, setChatMode] = useState<'discuss' | 'build'>('build');
    const [projectType, setProjectType] = useState<ProjectTabId>('landing');
    const projectTypeRef = useRef<ProjectTabId>('landing');
    const setProjectTypeWithRef = useCallback((type: ProjectTabId) => {
      setProjectType(type);
      projectTypeRef.current = type;
      // Sync to workbench store for tool filtering
      workbenchStore.setProjectType(type as 'landing');
    }, []);
    // Composer draft is scoped per chat id so one project's text can't leak
    // into another's input box (DEV-157). ChatImpl remounts per chat, so seeding
    // from the id's draft at mount is enough — no cross-project value survives.
    const { id: draftId } = useParams();

    // Local state for input (v6 useChat no longer manages input)
    const [input, setInput] = useState(() => getDraft(draftId));

    // Drop expired drafts and the legacy global `cachedPrompt` cookie once on load.
    useEffect(() => {
      pruneDrafts();
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
    }, []);

    const handleToggleAddToProject = useCallback((index: number) => {
      setAddToProject((prev) => {
        const next = [...prev];
        next[index] = !next[index];

        return next;
      });
    }, []);

    // Track previous file count to detect additions
    const prevFileCountRef = useRef(0);

    // Wrap setUploadedFiles to keep addToProject in sync when files are added
    const handleSetUploadedFiles = useCallback((files: File[]) => {
      const prevCount = prevFileCountRef.current;
      const newCount = files.length - prevCount;

      if (newCount > 0) {
        // Adding files - append false entries for new files
        setAddToProject((prev) => [...prev, ...Array(newCount).fill(false)]);
      }

      prevFileCountRef.current = files.length;
      setUploadedFiles(files);
    }, []);

    const handleSetImageDataList = useCallback((dataList: string[]) => {
      setImageDataList(dataList);
    }, []);

    // Process the brochure PDF in-browser as soon as it's attached. Artifacts
    // stay in memory until send — no chatId exists yet to upload against.
    const handleBrochureAttach = useCallback((file: File) => {
      if (!isPdfFile(file)) {
        toast.error('Brochures must be PDF files');
        return;
      }

      setBrochure({ file, status: 'processing' });

      processBrochurePdf(file, (progress) => {
        setBrochure((prev) => (prev?.file === file ? { ...prev, progress } : prev));
      })
        .then((artifacts) => {
          setBrochure((prev) => (prev?.file === file ? { file, status: 'ready', artifacts } : prev));
          capture(ANALYTICS_EVENTS.BROCHURE_ATTACHED, {
            pageCount: artifacts.pageCount,
            sizeBytes: file.size,
            embeddedPhotos: artifacts.embeddedImages.length,
          });
        })
        .catch((err) => {
          const reason = err instanceof BrochureError ? err.code : 'render-error';
          setBrochure((prev) =>
            prev?.file === file
              ? { file, status: 'error', error: err instanceof BrochureError ? err.message : "Couldn't read this PDF" }
              : prev,
          );
          capture(ANALYTICS_EVENTS.BROCHURE_PROCESS_FAILED, { reason, sizeBytes: file.size });
        });
    }, []);

    const handleBrochureRemove = useCallback(() => setBrochure(null), []);

    // PDFs attached as plain reference (not brochures): read each one's text in
    // the browser so it can be injected into the first message. No upload — the
    // text-first build model reads the content directly. Multiple are allowed so
    // a user can supply extra content PDFs alongside a brochure.
    const handlePdfAttach = useCallback((file: File) => {
      if (!isPdfFile(file)) {
        toast.error('Attachments read as text must be PDF files');
        return;
      }

      setPdfDocs((prev) => [...prev, { file, status: 'processing' }]);

      extractPdfText(file)
        .then((result) => {
          setPdfDocs((prev) => prev.map((d) => (d.file === file ? { file, status: 'ready', result } : d)));
        })
        .catch((err) => {
          setPdfDocs((prev) =>
            prev.map((d) =>
              d.file === file
                ? { file, status: 'error', error: err instanceof Error ? err.message : "Couldn't read this PDF" }
                : d,
            ),
          );
        });
    }, []);

    const handlePdfAttachRemove = useCallback(
      (file: File) => setPdfDocs((prev) => prev.filter((d) => d.file !== file)),
      [],
    );

    // When removing a file, also remove its addToProject entry
    const handleRemoveFile = useCallback((index: number) => {
      setUploadedFiles((prev) => {
        const next = prev.filter((_, i) => i !== index);
        prevFileCountRef.current = next.length;

        return next;
      });
      setImageDataList((prev) => prev.filter((_, i) => i !== index));
      setAddToProject((prev) => prev.filter((_, i) => i !== index));
    }, []);

    /*
     * Don't resume-subscribe on the mount that is itself about to send a stashed
     * message — the POST it fires IS the live stream. A concurrent resume GET
     * would connect as a second (Redis pub/sub) listener whose per-event publish
     * backpressures ai's read of the model provider, which coalesces streamed
     * tool-input into a single final frame (large file writes "appear all at
     * once"). Resume only for genuine reconnects (reload/reopen, no pending send).
     * Frozen at mount so a later re-render can't flip it and open a stray reconnect.
     */
    const resumeOnMount = useRef(shouldResume && !$pendingMessage.get()).current;

    // True while the deploy-safe reconnect loop below is retrying — lets
    // onError swallow the per-attempt failures (a full error alert stacked
    // under a "reconnecting…" toast is noise, not signal).
    const reconnectingRef = useRef(false);

    const {
      messages,
      sendMessage: chatSendMessage,
      stop,
      setMessages,
      regenerate,
      error,
      status,
      resumeStream,
    } = useChat({
      id: resolvedChatId,
      resume: resumeOnMount,
      // Coalesce UI updates. Critical for resume: reconnecting to an in-flight
      // generation replays the whole buffered stream as one burst, and without
      // throttling each event re-renders the growing message tree (O(n²) work
      // that froze the tab into the browser's "wait/kill" prompt). Also smooths
      // live streaming. ~20 renders/sec.
      experimental_throttle: 50,
      transport: new DefaultChatTransport({
        api: '/api/chat',
        /*
         * On mount (resume: true), useChat fires a GET to reconnect to an
         * in-flight generation. Point it at our resume route and carry the
         * bearer token (the custom `fetch` below still handles 401→refresh).
         */
        prepareReconnectToStreamRequest: ({ id }) => ({
          api: `/api/chat/${id}/stream`,
          credentials: 'include',
          headers: { Authorization: `Bearer ${$accessToken.get()}` },
        }),
        fetch: async (url, options) => {
          const token = $accessToken.get();

          // Inject mutable values at fetch time to avoid stale closure
          if (options?.body) {
            try {
              const body = JSON.parse(options.body as string);
              const currentChatId = chatId.get();

              if (!currentChatId && body.chatMode === 'build') {
                /*
                 * chatId is required for build mode — without it the server
                 * silently omits write/shell tools. Abort early so the user
                 * sees a clear error instead of the AI "planning" without executing.
                 */
                logger.error('chatId is not set at fetch time — aborting build request');
                throw new Error(JSON.stringify({ error: 'Chat ID not ready. Please try again.' }));
              }

              body.chatId = currentChatId;
              body.description = descriptionAtom.get();
              body.projectType = projectTypeRef.current;

              /*
               * Base64 the turn: it carries the whole project's files and the
               * message history, and Render's edge firewall 403s generated code
               * it mistakes for an attack. Studio decodes it (see encoded-json).
               */
              const headers = new Headers(options.headers);
              headers.set('Content-Type', ENCODED_JSON_CONTENT_TYPE);

              options = { ...options, headers, body: encodeJsonBody(body) };
            } catch {
              // body is not JSON, skip injection
            }
          }

          const makeRequest = async (t: string | null) => {
            let response: Response;

            if (t) {
              const headers = new Headers(options?.headers);
              headers.set('Authorization', `Bearer ${t}`);

              response = await fetch(url, { ...options, headers });
            } else {
              response = await fetch(url, options);
            }

            /*
             * Transport-level replay compaction, RESUME GET ONLY (never the
             * live build POST): fold the reconnect burst so useChat processes
             * dozens of chunks instead of thousands — the client stays
             * responsive even if a server-side fold is absent or regresses.
             */
            if (isResumeStreamUrl(url) && response.ok && response.body) {
              return new Response(foldResumeBody(response.body), {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
              });
            }

            return response;
          };

          const response = await makeRequest(token);

          /*
           * 202 = parked in the build queue, not started. The body is JSON, not
           * an SSE stream, so it must be caught here — handing it to the AI SDK
           * would have it parse a stream that will never arrive.
           *
           * Re-thrown as a JSON error so it lands in the existing onError
           * pipeline, which already parses this shape; onError recognises the
           * AI_QUEUED code and shows the waiting room instead of an alert.
           * Nothing ran and nothing was billed.
           */
          if (response.status === 202) {
            const queued = await response.json().catch(() => ({}));

            throw new Error(JSON.stringify({ ...queued, code: 'AI_QUEUED', statusCode: 202 }));
          }

          // If 401, try refreshing the access token and retry once
          if (response.status === 401) {
            try {
              const { accessToken: newToken } = await apiRefreshToken();
              $accessToken.set(newToken);

              return makeRequest(newToken);
            } catch {
              if (!$authLoading.get()) {
                clearAuth();
              }

              return response;
            }
          }

          return response;
        },
        body: {
          files,
          chatMode,
          projectType,
          designScheme,
        },
      }),
      onError: (e) => {
        setFakeLoading(false);

        // Reconnect attempts report their own outcome — don't stack full
        // error alerts under the "reconnecting…" toast.
        if (reconnectingRef.current) {
          logger.warn('stream reconnect attempt errored', e);
          return;
        }

        /*
         * Pre-send errors come from the resume reconnect (no user send yet).
         * A missing stream already returns 204 (no error), so this guards the
         * rare reconnect network failure — fall back silently to loaded messages.
         */
        if (!hasUserSentRef.current) {
          logger.warn('Ignoring pre-send stream error (likely resume reconnect)', e);
          return;
        }

        /*
         * A mid-stream transport drop while the server keeps generating is a
         * FALSE ALARM — the build is fine, only our SSE died (observed as the
         * "Server Error / network error" banner at 5–10 min into long builds
         * that then "work as usual"). Probe the server first:
         *
         *  - our turn is still running → reattach quietly (detectInterruptedStream
         *    shows its own "reconnecting…" toast and falls back to Resume if it
         *    can't) and never show a banner.
         *  - the probe went unanswered → OUR network is down, so the server was
         *    never asked. The build is almost certainly still running, so try to
         *    get back in rather than blame the server. This is the exact case
         *    that used to surface as "Server Error" over a healthy build.
         *  - the server confirms our turn is NOT running → a real failure.
         */
        void (async () => {
          const probe = await probeActiveGeneration();

          if (probe.reachable && isOurTurnStillRunning(probe.active, chatId.get())) {
            logger.warn('live stream dropped but server turn still running — reattaching quietly', e);
            void detectInterruptedStream();

            return;
          }

          if (!probe.reachable) {
            logger.warn('live stream dropped and the server is unreachable — reconnecting, not alarming', e);
            void detectInterruptedStream({ assumeRunning: true });

            return;
          }

          handleError(e, 'chat');
        })();
      },
      onFinish: ({ message }) => {
        logger.debug('Finished streaming');

        // Server pushes the post-deduction balance in message metadata — update displays instantly.
        const creditsMeta = (message.metadata as any)?.credits?.value;
        const remaining = creditsMeta?.creditsRemaining;

        if (typeof remaining === 'number') {
          setLiveCredits(remaining);
        }

        // Mid-build credit cutoff: the ceiling stopped the build before it
        // finished. Surface a tier-aware "top up to continue" alert (with a
        // Continue action) instead of letting a half-built site look complete.
        // This same `credits.value` rides along on the assistant message
        // metadata, so a reload re-surfaces the panel (see the effect below).
        if (creditsMeta?.creditsExhausted === true) {
          setLlmErrorAlert({
            type: 'warning',
            title: 'Out of credits — build paused',
            description: '',
            errorType: 'credits_exhausted',
            tier: creditsMeta.tier,
            midBuild: true,
          });
        }

        logStore.logProvider('Chat response completed', {
          component: 'Chat',
          action: 'response',
          model,
          provider: provider!.name,
          messageLength: (message.parts ?? [])
            .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
            .map((p) => p.text)
            .join('').length,
        });
      },
      messages: initialMessages,
    });

    // Derive isLoading from status for backward compatibility
    const isLoading = status === 'streaming' || status === 'submitted';

    // Pick up AI-generated chat title from stream metadata — always overwrite optimistic fallback
    useEffect(() => {
      for (const msg of messages) {
        const title = (msg.metadata as any)?.chatDescription;

        if (title && typeof title === 'string') {
          descriptionAtom.set(title);
          break;
        }
      }
    }, [messages]);

    const { enhancingPrompt, promptEnhanced, enhancePrompt, resetEnhancer, enhanceError, clearEnhanceError } =
      usePromptEnhancer();
    const { parseMessages } = useMessageParser();

    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

    useEffect(() => {
      if (initialMessages.length > 0) {
        chatStore.setKey('started', true);
      }
    }, []);

    // Sync project type from chat metadata to workbench store when loading existing chat
    const metadata = useStore(chatMetadata);

    useEffect(() => {
      if (metadata?.projectType) {
        const type: ProjectTabId = 'landing';
        setProjectType(type);
        projectTypeRef.current = type;
        workbenchStore.setProjectType(type);
      }
    }, [metadata?.projectType]);

    /*
     * Pick up a pending message stashed during new-chat navigation.
     * The old ChatImpl stored the text in $pendingMessage before the route
     * change unmounted it; now this fresh ChatImpl sends it for real.
     */
    useEffect(() => {
      const pending = $pendingMessage.get();

      if (pending && !$showAuthModal.get()) {
        /*
         * Mark the chat as started immediately so other code (sendMessage,
         * useChatHistory) knows a chat is active.
         */
        setChatStarted(true);
        chatStore.setKey('started', true);

        /*
         * Use a timeout + cleanup to survive React 18 Strict Mode.
         * In Strict Mode, effects run → cleanup → run again. The cleanup
         * cancels mount-1's timeout, so `$pendingMessage` is still intact
         * when mount-2's effect fires and schedules a NEW timeout that
         * references mount-2's sendMessageRef (the real, live instance).
         */
        const timeoutId = setTimeout(() => {
          $pendingMessage.set(null);
          sendMessageRef.current?.({} as React.UIEvent, pending);
        }, 100);

        return () => clearTimeout(timeoutId);
      }

      return undefined;
    }, []);

    /*
     * Landing-page prompt (dunea.ai hero composer → `/?prompt=`): start the
     * build the visitor already asked for. Decided once, when the session
     * restore settles — signed in sends it; signed out opens sign-in with it
     * queued, and the post-auth flush below sends it. A magic link reopens the
     * app in a new tab, where the still-set flag sends it on this same path.
     */
    useEffect(() => {
      if (draftId) {
        return undefined;
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const decide = () => {
        if (!hasLandingAutostart() || $pendingMessage.get()) {
          return;
        }

        const draft = getDraft(undefined).trim();

        if (!draft) {
          clearLandingAutostart();
          return;
        }

        if ($isAuthenticated.get()) {
          timeoutId = setTimeout(() => sendMessageRef.current?.({} as React.UIEvent, draft), 100);
        } else {
          $pendingMessage.set(draft);
          $showAuthModal.set(true);
        }
      };

      if (!$authLoading.get()) {
        decide();
        return () => clearTimeout(timeoutId);
      }

      const unsubscribe = $authLoading.subscribe((loading) => {
        if (!loading) {
          unsubscribe();
          decide();
        }
      });

      return () => {
        unsubscribe();
        clearTimeout(timeoutId);
      };
    }, []);

    // Checkpoint availability — refetched on chat load, after each completed
    // generation (the turn just created one), and after a restore.
    const refreshCheckpoints = useCallback(() => {
      const currentChatId = chatId.get();

      if (!currentChatId) {
        return;
      }

      listCheckpoints(currentChatId)
        .then((items) => setCheckpoints(new Set(items.map((c) => c.messageId))))
        .catch((err) => {
          logger.warn('Failed to load checkpoints', err);
        });
    }, []);

    useEffect(() => {
      if (resolvedChatId) {
        refreshCheckpoints();
      }
    }, [resolvedChatId, refreshCheckpoints]);

    const handleRestore = useCallback(
      async (messageId: string) => {
        const currentChatId = chatId.get();

        if (!currentChatId) {
          return;
        }

        setIsRestoring(true);
        toast.loading('Restoring…', { toastId: 'restore-checkpoint' });

        try {
          const { restoreMessageId } = await restoreCheckpoint(currentChatId, messageId);

          const restoreCard = {
            id: restoreMessageId,
            role: 'assistant',
            parts: [{ type: 'text', text: 'Restored the project to an earlier version.' }],
            metadata: { type: 'restore', targetMessageId: messageId },
          } as unknown as UIMessage;

          setMessages([...messages, restoreCard]);

          // Bump the preview reload nonce and re-sync the sandbox connection so
          // the code viewer / preview reflect the restored files — mirrors the
          // reboot the Preview panel itself falls back to after a snapshot
          // restore recreates the pod (see Preview.tsx's sleeping-pod handling).
          workbenchStore.previewReloadNonce.set(workbenchStore.previewReloadNonce.get() + 1);

          try {
            await runtime.boot(currentChatId, projectTypeRef.current);
          } catch (err) {
            logger.error('Failed to resync sandbox after restore', err);
          }

          refreshCheckpoints();
        } catch (err: any) {
          const code = err?.response?.data?.code;
          toast.error(mapRestoreErrorCode(code));
          captureException(err instanceof Error ? err : new Error(String(err)), {
            context: 'chat.restore_checkpoint',
            chatId: currentChatId,
            messageId,
          });
        } finally {
          toast.dismiss('restore-checkpoint');
          setIsRestoring(false);
        }
      },
      [messages, setMessages, refreshCheckpoints],
    );

    // Restore requested from the preview version selector. The banner CTA
    // can't call handleRestore directly (different tree), so it stamps the
    // atom and we consume it here — same guards, same flow as the chat
    // revert button.
    const restoreRequest = useStore($restoreRequest);

    useEffect(() => {
      if (!restoreRequest || isRestoring) {
        return;
      }

      $restoreRequest.set(null);
      exitPeek();
      handleRestore(restoreRequest);
    }, [restoreRequest, isRestoring, handleRestore]);

    const wasLoadingRef = useRef(false);

    // Live status readable from async loops without stale closures.
    const statusRef = useRef(status);
    statusRef.current = status;

    // Counts entries into 'streaming' — evidence that real tokens flowed. A
    // reattach is only proven by data arriving: a transient 'submitted' can
    // belong to a doomed resume request that never delivers.
    const streamStartsRef = useRef(0);
    const prevStatusRef = useRef(status);

    if (prevStatusRef.current !== 'streaming' && status === 'streaming') {
      streamStartsRef.current += 1;
    }

    prevStatusRef.current = status;

    // True once this session actually streamed — distinguishes "resume replayed
    // and finished" from "resume found nothing" in the mount check below.
    const everStreamedRef = useRef(false);

    if (status === 'streaming' || status === 'submitted') {
      everStreamedRef.current = true;
    }

    // Set when the interruption heuristics flipped `aborted` — lets a stream
    // that shows up late clear the flag it wrongly set.
    const heuristicAbortRef = useRef(false);
    const disposedRef = useRef(false);

    useEffect(
      () => () => {
        disposedRef.current = true;
        toast.dismiss('stream-reconnect');
      },
      [],
    );

    // A stream arriving after a heuristic flip proves the flip wrong — undo it.
    useEffect(() => {
      if (status === 'streaming' && heuristicAbortRef.current) {
        heuristicAbortRef.current = false;
        chatStore.setKey('aborted', false);
      }
    }, [status]);

    /*
     * apiGetActiveGeneration with one retry. During a studio deploy — the main
     * scenario these heuristics exist for — the first probe often hits the
     * restarting instance and fails; going dark there would blind the feature
     * exactly when it matters.
     */
    /*
     * Ask the server whether OUR turn is still generating. Returns `reachable`
     * alongside the answer so callers can tell "the server says nothing is
     * running" apart from "I never got an answer" — see ActiveGenerationProbe.
     */
    const probeActiveGeneration = useCallback(async (): Promise<ActiveGenerationProbe> => {
      try {
        return { reachable: true, active: await apiGetActiveGeneration() };
      } catch (err) {
        logger.warn('active-generation probe failed, retrying once', err);
        await new Promise((r) => setTimeout(r, 5000));

        try {
          return { reachable: true, active: await apiGetActiveGeneration() };
        } catch (retryErr) {
          logger.warn('active-generation probe failed twice — cannot verify server state', retryErr);
          return { reachable: false, active: null };
        }
      }
    }, []);

    /*
     * Flips the aborted state so the existing Resume button shows. In-memory
     * only by design: metadata.aborted is deliberately NOT persisted here (the
     * server's active-stream marker already makes a reload re-detect the
     * interruption; stamping messages from a heuristic risks corrupting a
     * healthy chat). Guarded against the user having navigated away — aborted
     * is a GLOBAL flag, and stamping it while another chat is active would
     * poison that chat's saves.
     */
    const surfaceInterrupted = useCallback((currentChatId: string, message: string) => {
      if (disposedRef.current || chatId.get() !== currentChatId || chatStore.get().aborted) {
        return;
      }

      heuristicAbortRef.current = true;
      chatStore.setKey('aborted', true);
      toast.error(message);
    }, []);

    /*
     * Deploy-safe reconnect. A mid-turn studio restart (or a network blip)
     * ends the SSE without a finish frame; the UI used to render that as a
     * clean DONE while the server kept generating (and the resume producer was
     * gone, so a reload's reconnect 204'd into the same stale view). Ask the
     * server whether OUR turn is still running; if so auto-reattach, and when
     * the stream is unrecoverable surface Resume instead of a false DONE.
     */
    const detectInterruptedStream = useCallback(
      async (opts?: { assumeRunning?: boolean }) => {
        const currentChatId = chatId.get();

        if (!currentChatId || reconnectingRef.current || chatStore.get().aborted) {
          return;
        }

        const probe = await probeActiveGeneration();

        /*
         * An unanswered probe is not a "no". When the caller already knows the
         * local connection dropped, assume the turn survived and try to get back
         * in: the loop below is capped (3 attempts / 30s) and idempotent, and it
         * gives up into a friendly Resume prompt — a far better wrong guess than
         * telling the user their build hit a server error.
         */
        const stillRunning = probe.reachable
          ? isOurTurnStillRunning(probe.active, currentChatId)
          : (opts?.assumeRunning ?? false);

        if (!stillRunning) {
          return;
        }

        reconnectingRef.current = true;

        try {
          toast.loading('Connection to the build was lost — reconnecting…', { toastId: 'stream-reconnect' });

          const isStale = () => disposedRef.current || chatId.get() !== currentChatId;
          const startStreams = streamStartsRef.current;
          const reattached = () => statusRef.current === 'streaming' || streamStartsRef.current > startStreams;
          const deadline = Date.now() + 30_000;
          let attemptsLeft = 3;
          let attemptSettled = true;

          while (Date.now() < deadline) {
            if (isStale() || reattached()) {
              break;
            }

            /*
             * Fire the next attempt only when the previous one has settled and
             * nothing is pending ('submitted') — overlapping resume listeners
             * backpressure the producer and coalesce its output (see the
             * resumeOnMount comment above).
             */
            if (attemptSettled && attemptsLeft > 0 && statusRef.current !== 'submitted') {
              const attempt = 4 - attemptsLeft;
              attemptsLeft -= 1;
              attemptSettled = false;

              resumeStream()
                .catch((err) => logger.warn(`stream reconnect attempt ${attempt} failed`, err))
                .finally(() => {
                  attemptSettled = true;
                });
            }

            await new Promise((r) => setTimeout(r, 1000));

            // Every attempt spent and settled with no pending request and no
            // data — waiting out the deadline would change nothing.
            if (attemptsLeft === 0 && attemptSettled && statusRef.current !== 'submitted' && !reattached()) {
              break;
            }
          }

          toast.dismiss('stream-reconnect');

          if (isStale() || reattached()) {
            return; // reattached (its own end-of-stream cycle takes over) or not our chat anymore
          }

          logger.warn('stream reconnect gave up without data — surfacing Resume');
          surfaceInterrupted(currentChatId, 'The build was interrupted — hit Resume to continue where it left off.');
        } finally {
          reconnectingRef.current = false;
        }
      },
      [resumeStream, probeActiveGeneration, surfaceInterrupted],
    );

    /*
     * Mount-time resume that finds nothing: the chat loaded with an active
     * stream marker, but the reconnect never started streaming (dead producer —
     * the exact aftermath of a mid-turn deploy; the 204 also clears the
     * marker). Verify with the server before alarming, and give a slow
     * reconnect handshake a second window before concluding anything.
     */
    useEffect(() => {
      if (!resumeOnMount) {
        return undefined;
      }

      let windows = 0;
      let timer: ReturnType<typeof setTimeout>;

      const check = async () => {
        if (disposedRef.current || everStreamedRef.current || chatStore.get().aborted) {
          return;
        }

        // Reconnect request still pending — slow handshake, not a dead producer.
        if (statusRef.current === 'submitted' && windows < 2) {
          windows += 1;
          timer = setTimeout(check, 6000);

          return;
        }

        const currentChatId = chatId.get();

        if (!currentChatId) {
          return;
        }

        const probe = await probeActiveGeneration();

        if (probe.reachable && isOurTurnStillRunning(probe.active, currentChatId)) {
          // Turn is alive server-side — try to reattach rather than alarm.
          void detectInterruptedStream().catch((err) => logger.error('interrupted-stream detection crashed', err));

          return;
        }

        surfaceInterrupted(currentChatId, 'This build was interrupted — hit Resume to continue where it left off.');
      };

      timer = setTimeout(check, 6000);

      return () => clearTimeout(timer);
    }, [resumeOnMount, probeActiveGeneration, detectInterruptedStream, surfaceInterrupted]);

    /*
     * Live-stream self-heal. detectInterruptedStream (above) only runs when the
     * stream ENDS — isLoading flips true→false. But a backgrounded tab or a
     * dropped connection during a long model silence freezes the SSE with NO
     * finish, error, or close event, so isLoading stays true forever and nothing
     * ever probes: a phantom "generating" spinner over a turn the server already
     * finished (observed 2026-08-12, and made likelier after the server stall
     * timeout was raised to 4 min — longer silences mean longer heartbeat-only
     * gaps for a middlebox/backgrounded tab to drop). This watchdog runs WHILE
     * still loading.
     */
    // Last time the live stream produced anything (updated in the messages
    // effect below). Drives the idle backstop.
    const lastStreamActivityRef = useRef(Date.now());
    // Set when we finalize a stuck-but-done stream: the server already persisted
    // the authoritative turn, so the post-stop save must NOT overwrite it with
    // our partial client copy.
    const skipStuckSaveRef = useRef(false);

    const resyncStuckStream = useCallback(
      async (allowReattach: boolean) => {
        const currentChatId = chatId.get();

        if (
          !currentChatId ||
          reconnectingRef.current ||
          chatStore.get().aborted ||
          (statusRef.current !== 'streaming' && statusRef.current !== 'submitted')
        ) {
          return;
        }

        const probe = await probeActiveGeneration();

        /*
         * Probe went unanswered — can't verify server state; leave a possibly
         * healthy stream untouched rather than false-heal. Note this used to
         * test `active === null`, which ALSO matched the server answering "no
         * turn is running" — so the finalize path below could never fire and
         * the phantom spinner it exists to clear stayed on screen.
         */
        if (!probe.reachable) {
          return;
        }

        if (isOurTurnStillRunning(probe.active, currentChatId)) {
          // Server is still generating but our client went quiet — the live
          // connection is probably dead. Reattach (capped, idempotent handshake).
          // Only on strong "connection dropped" signals (visible/online); the
          // idle backstop passes allowReattach=false so a genuinely-live-but-
          // model-silent stream is never disturbed.
          if (allowReattach) {
            void detectInterruptedStream().catch((err) => logger.error('resync reattach crashed', err));
          }

          return;
        }

        // Server turn is DONE, yet the client is still "generating": the finish
        // frame never arrived. stop() aborts the dead local stream, flipping
        // isLoading true→false — which fires the existing finish effect (preview
        // reload, checkpoint refresh, switch to Preview), clearing the phantom
        // spinner and showing the built site with no manual reload.
        logger.warn('[stream-recovery] loading but server turn is done — finalizing stuck stream');
        skipStuckSaveRef.current = true;
        stop();
      },
      [probeActiveGeneration, detectInterruptedStream, stop],
    );

    // Fire the self-heal on the signals that a live connection was likely lost.
    // A tab returning to the foreground or the network coming back get a full
    // resync (reattach if running, finalize if done).
    //
    // The idle backstop is HEARTBEAT-BASED. The server writes a `data-aiHeartbeat`
    // part every 20s (AI_HEARTBEAT_INTERVAL_MS) for the whole turn — including
    // while luna is silently thinking — and non-transient data parts land in
    // `messages`, so `lastStreamActivityRef` (bumped on every messages change) is
    // refreshed every 20s on ANY healthy connection. That lets us distinguish a
    // DEAD connection (no heartbeats) from a live-but-thinking one (heartbeats
    // still flowing) WITHOUT waiting out luna's 300s think budget: 3+ missed
    // heartbeats (~70s of true silence) means the connection is gone. It stays
    // FINALIZE-ONLY (allowReattach=false), so even in the impossible case that
    // heartbeats aren't updating activity, a live turn only gets a harmless probe
    // that no-ops — never a disruptive reattach.
    useEffect(() => {
      // ~3.5 missed 20s heartbeats. Above jitter, far below the 5.5-min blind
      // timeout this replaces — "generating after done" now clears in ~70-85s.
      const HEARTBEAT_GAP_MS = 70_000;

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          void resyncStuckStream(true);
        }
      };
      const onOnline = () => void resyncStuckStream(true);

      const idle = setInterval(() => {
        if (
          (statusRef.current === 'streaming' || statusRef.current === 'submitted') &&
          Date.now() - lastStreamActivityRef.current > HEARTBEAT_GAP_MS
        ) {
          void resyncStuckStream(false);
        }
      }, 15_000);

      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('online', onOnline);

      return () => {
        clearInterval(idle);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('online', onOnline);
      };
    }, [resyncStuckStream]);

    useEffect(() => {
      processSampledMessages({
        messages,
        initialMessages,
        isLoading,
        parseMessages,
      });

      // This effect runs on every message/status change — while streaming, that
      // IS the stream's heartbeat of activity, so stamp it for the idle backstop
      // (resyncStuckStream). A frozen connection stops producing changes, so the
      // stamp goes stale and the backstop eventually fires.
      if (isLoading) {
        lastStreamActivityRef.current = Date.now();
      }

      // Generation just finished (streaming true → false). Bump the preview
      // reload nonce so the Preview panel reloads the freshly built site — it is
      // unmounted while the code tab is shown during a build, so it otherwise
      // misses the reload and keeps displaying the pre-build page until a manual
      // refresh.
      if (wasLoadingRef.current && !isLoading) {
        workbenchStore.previewReloadNonce.set(workbenchStore.previewReloadNonce.get() + 1);

        // The turn that just finished created a new checkpoint — refresh availability.
        refreshCheckpoints();

        // …unless it didn't finish at all: if the server still reports this
        // chat as generating, the stream dropped mid-turn — reattach or
        // surface Resume instead of a false DONE.
        void detectInterruptedStream().catch((err) => logger.error('interrupted-stream detection crashed', err));

        // Surface the finished result: switch the workbench to the Preview tab.
        // During a build the Code tab is shown as files stream in, so the
        // reloaded preview lands out of view and the user can miss that it's
        // done. Build-only: a discuss reply builds nothing, so switching would
        // just yank a user off the Code tab they're reading. Skip on a user
        // Stop too — a manual abort shouldn't move them off what they're on.
        if (chatMode === 'build' && !chatStore.get().aborted) {
          workbenchStore.setActiveTab('preview');
        }
      }

      // Save snapshot once streaming finishes (isLoading: true → false). If this
      // fires after an abort, stamp metadata.aborted so it can't re-clobber the
      // resumability flag the server's own save sets on the same chat row.
      if (wasLoadingRef.current && !isLoading && skipStuckSaveRef.current) {
        // We finalized a stuck-but-done stream (resyncStuckStream): the server
        // already persisted the authoritative turn. Saving our partial client
        // copy here would clobber it — so skip exactly this one save. A reload
        // re-reads the server's complete version.
        skipStuckSaveRef.current = false;
      } else if (wasLoadingRef.current && !isLoading && messages.length > initialMessages.length) {
        const messagesToSave = chatStore.get().aborted ? withAbortedMetadata(messages) : messages;
        storeMessageHistory(messagesToSave).catch((err) => {
          console.error('Failed to save chat, retrying...', err);
          captureRequestError(err, {
            context: 'chat.save.post_stream.first_attempt',
            messageCount: messages.length,
            initialMessageCount: initialMessages.length,
            pathname: window.location.pathname,
          });

          /*
           * Retry once the request can actually go through. A phone that locked
           * mid-build freezes the tab, so a fixed 2s retry failed the same way and
           * told the owner their work wasn't saved (the server saves the turn
           * itself). Wait for the tab to be back in front and online.
           */
          whenReachable()
            .then(() => new Promise((resolve) => setTimeout(resolve, 2000)))
            .then(() => storeMessageHistory(messagesToSave))
            .catch((retryErr) => {
              console.error('Retry failed:', retryErr);
              captureRequestError(retryErr, {
                context: 'chat.save.post_stream.retry',
                messageCount: messages.length,
                initialMessageCount: initialMessages.length,
                pathname: window.location.pathname,
                userVisibleToast: !isNetworkError(retryErr),
              });

              if (!isNetworkError(retryErr)) {
                toast.error("Couldn't save automatically. Send another message to try again.");
              }
            });
        });
      }

      wasLoadingRef.current = isLoading;
    }, [messages, isLoading, parseMessages, refreshCheckpoints, detectInterruptedStream]);

    // Periodic auto-save during streaming to prevent data loss
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    useEffect(() => {
      if (!isLoading) {
        return undefined;
      }

      const autoSaveInterval = setInterval(() => {
        const current = messagesRef.current;

        // A hidden (locked / backgrounded) or offline tab can't reach the server;
        // the next tick — or the save when streaming ends — covers it.
        if (current.length > initialMessages.length && canReachServer()) {
          storeMessageHistory(current).catch((err) => {
            console.error('Auto-save during streaming failed:', err);
            captureRequestError(err, {
              context: 'chat.save.auto_during_stream',
              messageCount: current.length,
              initialMessageCount: initialMessages.length,
              pathname: window.location.pathname,
            });
          });
        }
      }, 30_000);

      return () => clearInterval(autoSaveInterval);
    }, [isLoading, initialMessages.length, storeMessageHistory]);

    // Warn user before closing tab during active streaming
    useEffect(() => {
      if (!isLoading) {
        return undefined;
      }

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isLoading]);

    const scrollTextArea = () => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.scrollTop = textarea.scrollHeight;
      }
    };

    const abort = () => {
      stop();
      chatStore.setKey('aborted', true);
      workbenchStore.abortAllActions();

      const cancelId = chatId.get();

      if (cancelId) {
        cancelGeneration(cancelId).catch((err) => {
          console.error('cancelGeneration failed:', err);
          captureRequestError(err, {
            context: 'chat.cancel_generation',
            chatId: cancelId,
          });
          toast.error('Could not stop the generation. Please try again.');
        });
      }

      $activeGeneration.set(null);

      // Save whatever messages we have so far so the chat isn't lost. Stamp
      // metadata.aborted so this save can't erase the resumability flag the
      // server's own (authoritative) save of the same chat row sets.
      if (messages.length > initialMessages.length) {
        storeMessageHistory(withAbortedMetadata(messages)).catch((err) => {
          console.error('Failed to save chat on abort:', err);
          captureRequestError(err, {
            context: 'chat.save.on_abort',
            messageCount: messages.length,
            initialMessageCount: initialMessages.length,
            pathname: window.location.pathname,
          });
        });
      }

      logStore.logProvider('Chat response aborted', {
        component: 'Chat',
        action: 'abort',
        model,
        provider: provider!.name,
      });
    };

    const CONTINUE_PROMPT = continuePromptMd.trim();

    // Resumable when the last assistant turn was stopped: live (this session
    // set chatStore.aborted) or persisted (metadata.aborted stamped by the
    // server's finalize or the client's abort-path saves).
    const lastMessage = messages[messages.length - 1];
    const canResume =
      !isLoading &&
      (chatStore.get().aborted ||
        (lastMessage?.role === 'assistant' && (lastMessage.metadata as any)?.aborted === true));

    const handleResume = () => {
      // Dismiss the out-of-credits panel (if any) — the continuation is a fresh
      // turn that will re-surface it only if it, too, runs out.
      setLlmErrorAlert(undefined);

      // Phase E2: attach a done/remaining progress note from the paused build's
      // plan so the resuming turn skips re-deriving what's already finished.
      const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
      const tasks = latestTaskUpdate(lastAssistant?.metadata as any, (lastAssistant as any)?.annotations);

      // The agent receives the full instructions + progress note; the hidden
      // marker makes the chat render a compact "Continue" chip instead.
      const resumeText = `${RESUME_BUILD_MARKER}\n\n${buildResumeContinuation(CONTINUE_PROMPT, tasks)}`;
      sendMessage(new Event('resume') as any, resumeText);
    };

    // Re-surface the "out of credits" panel after a reload. onFinish sets it
    // live, but that fires only during the streaming session; on a fresh mount
    // the state is gone. The credit cutoff is durably persisted on the streaming
    // assistant message as `metadata.credits.value.creditsExhausted` (the same
    // signal onFinish reads), so scan back to the LAST ASSISTANT message — a
    // blocked follow-up send after the cutoff leaves trailing user messages, so
    // the last message isn't necessarily the assistant one. Fire once; guarded
    // so a dismissal (or a later resume) isn't undone on every render.
    const creditsExhaustedShownRef = useRef(false);
    useEffect(() => {
      if (isLoading || creditsExhaustedShownRef.current) {
        return;
      }

      const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
      const credits = (lastAssistant?.metadata as any)?.credits?.value;

      if (credits?.creditsExhausted === true) {
        creditsExhaustedShownRef.current = true;
        setLlmErrorAlert({
          type: 'warning',
          title: 'Out of credits — build paused',
          description: '',
          errorType: 'credits_exhausted',
          tier: credits.tier,
          midBuild: true,
        });
      }
    }, [isLoading, messages]);

    const handleError = useCallback(
      (error: any, context: 'chat' | 'template' | 'llmcall' = 'chat') => {
        logger.error(`${context} request failed`, error);

        stop();
        setFakeLoading(false);

        let errorInfo = {
          message: 'An unexpected error occurred',
          isRetryable: true,
          /*
           * 0 = "no HTTP status", NOT 500. Every server error body carries an
           * explicit statusCode (see the ai controller's errorResponse), so
           * anything still sitting at 0 never got a response at all — defaulting
           * it to 500 blamed the server for browser-side transport failures.
           */
          statusCode: 0,
          provider: provider!.name,
          type: 'unknown' as const,
          retryDelay: 0,
          code: '' as string,
        };

        if (error.message) {
          try {
            const parsed = JSON.parse(error.message);

            if (parsed.error || parsed.message) {
              errorInfo = { ...errorInfo, ...parsed };
            } else {
              errorInfo.message = error.message;
            }
          } catch {
            errorInfo.message = error.message;
          }
        }

        /*
         * Render's edge firewall answers a request it takes for an attack with an
         * HTML "Blocked" page — on the browser → studio hop directly, or relayed
         * by studio from the studio → ai hop. Never dump that markup into the
         * alert. Encoded bodies should make this unreachable; this is the net.
         */
        if (isFirewallBlockPage(errorInfo.message)) {
          errorInfo = {
            ...errorInfo,
            message: 'This request was blocked by our hosting provider’s firewall. Please try again.',
            code: 'REQUEST_BLOCKED',
          };
        }

        /*
         * Parked in the build queue — an accepted request waiting its turn, not
         * a failure. Show the place in line; the store keeps it current over the
         * websocket (with a poll fallback) and re-sends automatically when the
         * server admits us. Deliberately ahead of every error branch below so a
         * queued build can never be rendered as an error.
         */
        if (errorInfo.code === 'AI_QUEUED') {
          const queuedChatId = (errorInfo as any).chatId || chatId.get();

          if (queuedChatId) {
            enterQueue(queuedChatId, {
              position: (errorInfo as any).position ?? 1,
              total: (errorInfo as any).total ?? 1,
              etaSec: (errorInfo as any).etaSec ?? 0,
            });
          }

          return;
        }

        // Cap backstop: server rejected a concurrent generation (409). Offer a
        // jump to the running thread instead of a scary error alert.
        if (errorInfo.code === 'GENERATION_IN_PROGRESS') {
          const running = (errorInfo as any).running as { id: string; urlId?: string | null } | undefined;

          /*
           * Same-chat rejection = a duplicate re-send (reconnect blip /
           * double-fire) of a turn that is STILL streaming server-side — the
           * exact case ai's duplicate-start guard exists for. Reattach to the
           * live stream instead of leaving the UI idle on the very page the
           * user is watching. detectInterruptedStream re-probes the server and
           * resumes with all the reconnect guards.
           */
          if (running && isOurTurnStillRunning(running as any, chatId.get())) {
            toast.info('This build is already running — reconnecting to it…', { autoClose: 4000 });
            void detectInterruptedStream().catch((err) => logger.error('409 stream reattach crashed', err));

            return;
          }

          toast.info('A build is already running. Open it or wait for it to finish before starting another.', {
            onClick: running ? () => navigate(`/chat/${running.urlId || running.id}`) : undefined,
            autoClose: 6000,
          });

          return;
        }

        let errorType: LlmErrorAlertType['errorType'] = 'unknown';
        let title = 'Request Failed';
        let tier: string | undefined;

        if (isTransportFailure(error, errorInfo.message)) {
          // Never reached the server — our connection, not their fault.
          errorType = 'network';
          title = 'Connection lost';
        } else if (errorInfo.code === 'REQUEST_BLOCKED') {
          title = 'Request Blocked';
        } else if (errorInfo.statusCode === 402 || errorInfo.code === 'CREDITS_EXHAUSTED') {
          errorType = 'credits_exhausted';
          title = 'No Credits Remaining';
          tier = (errorInfo as any).tier;
        } else if (errorInfo.statusCode === 403 || errorInfo.code === 'MODEL_TIER_RESTRICTED') {
          errorType = 'model_restricted';
          title = 'Model Not Available';
        } else if (errorInfo.statusCode === 401 || errorInfo.message.toLowerCase().includes('api key')) {
          errorType = 'authentication';
          title = 'Authentication Error';
        } else if (errorInfo.statusCode === 429 || errorInfo.message.toLowerCase().includes('rate limit')) {
          errorType = 'rate_limit';
          title = 'Rate Limit Exceeded';
        } else if (errorInfo.message.toLowerCase().includes('quota')) {
          errorType = 'quota';
          title = 'Quota Exceeded';
        } else if (errorInfo.code === 'AI_AT_CAPACITY') {
          // Admission-control shed: transient, nothing started or billed.
          // Distinct from a real outage — retry-flavored alert, not "Server
          // Error".
          errorType = 'capacity';
          title = 'AI at Capacity';
        } else if (errorInfo.statusCode >= 500) {
          errorType = 'server';
          title = 'Something went wrong on our end';
        }

        logStore.logError(`${context} request failed`, error, {
          component: 'Chat',
          action: 'request',
          error: errorInfo.message,
          context,
          retryable: errorInfo.isRetryable,
          errorType,
          provider: provider!.name,
        });

        // Create API error alert
        setLlmErrorAlert({
          type: 'error',
          title,
          description:
            errorType === 'credits_exhausted'
              ? "You've used all your credits for this billing period."
              : errorType === 'network'
                ? // Raw transport messages ("Network Error", "Failed to fetch")
                  // are browser noise — the copy above already says it better,
                  // and an empty description hides the mono detail block.
                  ''
                : errorInfo.message,
          provider: provider!.name,
          errorType,
          tier,
        });
      },
      [provider?.name, stop, detectInterruptedStream],
    );

    const clearApiErrorAlert = useCallback(() => {
      setLlmErrorAlert(undefined);
    }, []);

    const retryLastMessage = useCallback(() => {
      setLlmErrorAlert(undefined);

      if (messages.length > 0) {
        // Remove the failed assistant message if present, then reload
        const lastMsg = messages[messages.length - 1];

        if (lastMsg.role === 'assistant') {
          setMessages(messages.slice(0, -1));
        }

        regenerate();
      }
    }, [messages, setMessages, regenerate]);

    // Phase C — live credit counter. As the build streams `data-creditsLive` parts,
    // tick the sidebar balance down in real time. Display only: the authoritative
    // balance still arrives in the final credits metadata frame (onFinish).
    //
    // Only apply this while a turn is actively streaming. `data-creditsLive` parts are
    // persisted in message history, so without this guard, merely *opening* an old chat
    // replays that chat's last frame and clobbers the global balance with a stale
    // per-chat snapshot — making the sidebar number jump around as you switch chats
    // (it even appears to go *up* when opening an older chat). The balance is one global
    // value for the account; history viewing must never mutate it.
    useEffect(() => {
      if (!isLoading) {
        return;
      }

      const last = messages[messages.length - 1];

      if (last?.role !== 'assistant' || !Array.isArray((last as any).parts)) {
        return;
      }

      const parts = (last as any).parts as any[];

      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];

        if (p?.type === 'data-creditsLive' && p.data && typeof p.data.creditsRemaining === 'number') {
          setLiveCredits(p.data.creditsRemaining);

          return;
        }
      }
    }, [messages, isLoading]);

    useEffect(() => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.style.height = 'auto';

        const scrollHeight = textarea.scrollHeight;
        const maxHeight = composerMaxHeight(TEXTAREA_MAX_HEIGHT);

        textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      }
    }, [input, textareaRef]);

    const runAnimation = async () => {
      if (chatStarted) {
        return;
      }

      try {
        await Promise.all([
          animate('#examples', { opacity: 0, display: 'none' }, { duration: 0.1 }),
          animate('#intro', { opacity: 0, flex: 1 }, { duration: 0.2, ease: cubicEasingFn }),
        ]);
      } catch {
        // Elements may not exist if chat was already started — safe to ignore
      }

      chatStore.setKey('started', true);

      setChatStarted(true);
    };

    // When auth state flips false → true, re-fire any pending message that was
    // captured before opening the auth modal. AuthModal is mounted globally
    // (App.tsx), so this effect is the post-auth flush regardless of which
    // surface triggered the modal.
    useEffect(() => {
      let wasAuthenticated = $isAuthenticated.get();

      const unsubscribe = $isAuthenticated.subscribe((isAuthenticated) => {
        // Only flush on the false → true transition. This skips nanostores'
        // initial-value emission AND intentionally won't flush if the user was
        // already authenticated on mount — $pendingMessage is only set right
        // before opening the modal, so an already-authenticated mount has no
        // pending text to flush.
        if (!wasAuthenticated && isAuthenticated) {
          const pending = $pendingMessage.get();

          if (pending) {
            $pendingMessage.set(null);
            // Small delay to let auth state propagate to API client
            setTimeout(() => {
              sendMessageRef.current?.({} as React.UIEvent, pending);
            }, 100);
          }
        }

        wasAuthenticated = isAuthenticated;
      });

      return () => unsubscribe();
    }, []);

    /*
     * Start the build the moment the queue admits us.
     *
     * `regenerate()` rather than a fresh send: the user's message was already
     * appended to the transcript when the original request went out — it was
     * the REQUEST that got parked, not the message. Re-sending would append a
     * duplicate of what the user typed. regenerate re-fires the request against
     * the messages already there, which is exactly the retry we want.
     *
     * Leaving the queue on unmount matters as much as joining it: a user who
     * closes the tab or navigates away must not keep a place, or everyone
     * behind them waits on a ghost. (The server's TTL is the real backstop —
     * this just makes it immediate.)
     */
    useEffect(() => {
      setQueueAdmitHandler(() => {
        void regenerate();
      });

      return () => {
        setQueueAdmitHandler(null);

        if ($turnQueue.get().chatId) {
          leaveQueue();
        }
      };
    }, [regenerate]);

    const sendMessage = async (_event: React.UIEvent, messageInput?: string) => {
      const messageContent = messageInput || input;

      if (!messageContent?.trim()) {
        return;
      }

      // Gate: if not authenticated, show modal and save message
      if (!$isAuthenticated.get()) {
        $pendingMessage.set(messageContent);
        $showAuthModal.set(true);

        return;
      }

      // A real send — the landing prompt (if any) is now in flight or replaced.
      clearLandingAutostart();

      // An attachment still being read in the browser — sending now would drop it
      // (its text/artifacts only ride along on this first message) and clear its chip.
      if (brochure?.status === 'processing' || brochure?.status === 'uploading') {
        toast.info('Still reading your brochure — one moment…');
        return;
      }

      if (pdfDocs.some((doc) => doc.status === 'processing')) {
        toast.info('Still reading your PDF — one moment, then send again.');
        return;
      }

      // From here a real send is underway — stream errors are now user-facing.
      hasUserSentRef.current = true;

      /*
       * Cap: one generation per user. If a DIFFERENT chat is already streaming,
       * block and offer to jump to it. (The server also enforces this with a 409;
       * this pre-check just avoids the round-trip and gives a cleaner message.)
       * A follow-up in the same running chat is allowed (it aborts-and-resends).
       */
      const active = $activeGeneration.get();

      if (active && active.id !== chatId.get()) {
        toast.info('A build is already running. Open it or wait for it to finish before starting another.', {
          onClick: () => navigate(`/chat/${active.urlId || active.id}`),
          autoClose: 6000,
        });

        return;
      }

      /*
       * Nudge the active-generation store locally so the "generating" badge and
       * the client-side cap pre-check reflect this send immediately, instead of
       * waiting up to one poll interval. The poller reconciles id/urlId/description.
       */
      const liveChatId = chatId.get();

      if (liveChatId) {
        $activeGeneration.set({ id: liveChatId, urlId: liveChatId, description: null });
      }

      capture(ANALYTICS_EVENTS.MESSAGE_SENT, { model, provider: provider?.name });
      addExceptionStep('message_sent', { model, provider: provider?.name, chatMode });

      if (isLoading) {
        abort();

        // Wait briefly for abort to settle, then continue sending the new message
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      const finalMessageContent = messageContent;

      /*
       * Use the nanostore value instead of the React state `chatStarted` to
       * avoid stale-closure bugs.  The pending-message effect calls
       * `chatStore.setKey('started', true)` synchronously, but the React
       * state captured by this closure may still be `false` on the first
       * render, causing an infinite new-chat → navigate → new-chat loop.
       */
      const isStarted = chatStore.get().started;

      // For a brand new chat, force a fresh chatId so we never reuse an old pod
      if (!isStarted) {
        chatId.set(undefined);

        if (!descriptionAtom.get()) {
          const optimisticTitle = deriveOptimisticChatDescriptionFromText(messageContent);

          if (optimisticTitle) {
            descriptionAtom.set(optimisticTitle);
          }
        }
      }

      // Show loading state for new chats (boot + navigate takes a few seconds)
      if (!isStarted) {
        setIsStartingChat(true);
      }

      // Ensure chatId is generated before streaming starts so backend can save
      const id = await ensureChatId();

      // Boot the sandbox pod if the socket isn't already connected for this chat
      // — abort send if it fails. 'starting' counts as connected (socket joined,
      // dev server still coming up): re-booting a 'starting' pod would tear down
      // the live socket for nothing (DEV-155).
      const sandboxStatus = runtime.status.get();
      const needsBoot =
        !isStarted ||
        !runtime.isBooted() ||
        (sandboxStatus !== 'running' && sandboxStatus !== 'starting') ||
        runtime.getChatId() !== id;

      if (needsBoot) {
        // Reset workbench state when switching to a new chat
        workbenchStore.resetForNewChat();
        addExceptionStep('sandbox_boot_started', { chatId: id, projectType: projectTypeRef.current });
        setSandboxBootFailed(false);

        try {
          await runtime.boot(id, projectTypeRef.current);
          addExceptionStep('sandbox_boot_completed', { chatId: id });
        } catch (err: any) {
          console.error('Failed to boot sandbox:', err);
          setSandboxBootFailed(true);

          // Capture the boot failure so it reaches PostHog (→ Slack). Without this
          // the sandbox-init failure was silent — the surrounding addExceptionStep
          // breadcrumbs attached to an exception that was never emitted (DEV-146).
          captureException(err instanceof Error ? err : new Error(String(err)), {
            context: 'chat.sandbox_boot',
            chatId: id,
            projectType: projectTypeRef.current,
          });
          capture(ANALYTICS_EVENTS.SANDBOX_BOOT_FAILED, {
            chatId: id,
            stage: 'client_boot',
            errorMessage: err instanceof Error ? err.message : String(err),
          });

          toast.error("Couldn't start your workspace. Please try again.");
          setIsStartingChat(false);

          return;
        }
      }

      if (!isStarted) {
        /*
         * Upload the brochure artifacts BEFORE the route change: $pendingMessage
         * is a plain string, so everything the server needs must be embedded as
         * text markers in the stashed message. The chatId and booted pod already
         * exist at this point.
         */
        let stashContent = finalMessageContent;

        if (brochure?.status === 'ready' && brochure.artifacts) {
          setBrochure((prev) => (prev ? { ...prev, status: 'uploading' } : prev));

          try {
            const markers = await uploadBrochureArtifacts(id, brochure.file, brochure.artifacts);

            stashContent = `${markers.join('\n')}\n\n${finalMessageContent}`;

            // Vision on the page images gets the *design* right; text extraction
            // gets the *content/copy* right (body text is unreliable read out of an
            // image). Give the model both. Best-effort — never fail the build on a
            // text-extraction error, the design pipeline is what matters here.
            try {
              const { text, pageCount, truncated } = await extractPdfText(brochure.file);
              const name = brochure.file.name.replace(/"/g, "'");
              // source="brochure" so the UI shows the brochure card, not a second chip.
              const header = `[ATTACHED_PDF name="${name}" pages="${pageCount}" truncated="${truncated}" source="brochure"]`;
              stashContent = `${header}\n${text}\n[/ATTACHED_PDF]\n\n${stashContent}`;
            } catch (textErr) {
              console.warn('Brochure text extraction failed (continuing with vision only):', textErr);
            }

            setBrochure(null);
          } catch (err: any) {
            console.error('Failed to upload brochure:', err);
            toast.error('Brochure upload failed. Remove it or try sending again.');
            capture(ANALYTICS_EVENTS.BROCHURE_PROCESS_FAILED, {
              reason: 'upload-error',
              sizeBytes: brochure.file.size,
              httpStatus: err?.response?.status ?? null,
            });
            setBrochure((prev) => (prev ? { ...prev, status: 'ready' } : prev));
            setIsStartingChat(false);

            return;
          }
        }

        // Plain PDF attachments ride along as text — prepend each one's extracted
        // content to the stashed message so the build model reads it directly.
        // Each is wrapped in an [ATTACHED_PDF]…[/ATTACHED_PDF] marker: the server
        // passes it straight to the model, while the chat UI strips the marker
        // and shows just a PDF chip (see UserMessage.tsx).
        for (const doc of pdfDocs) {
          if (doc.status !== 'ready' || !doc.result) {
            continue;
          }

          const name = doc.file.name.replace(/"/g, "'");
          const { text, pageCount, truncated } = doc.result;
          const header = `[ATTACHED_PDF name="${name}" pages="${pageCount}" truncated="${truncated}" source="attachment"]`;
          stashContent = `${header}\n${text}\n[/ATTACHED_PDF]\n\n${stashContent}`;
        }
        setPdfDocs([]);

        /*
         * Stash the raw image files across the route change. $pendingMessage is a
         * string and can't carry File objects, but this in-memory atom can (same
         * JS runtime — navigation is client-side, no reload). The freshly-mounted
         * ChatImpl uploads and sends them through the exact same path an existing
         * chat uses, so the first message gets identical vision handling.
         */
        if (uploadedFiles.length > 0) {
          $pendingFiles.set(uploadedFiles);
        }

        // Store project type in chat metadata for the Recent Tasks list. Set it
        // before the commit below so the persisted row carries the type.
        const newChatMetadata = { ...chatMetadata.get(), projectType: projectType as any };
        chatMetadata.set(newChatMetadata);

        // Link to the active project (set by the project-detail "new chat" flow),
        // then consume it so the later stream save can't double-link.
        const activeProject = $activeProjectId.get();

        /*
         * Commit the chat row to the server NOW — before navigating — so the
         * project is addressable the instant its /chat/:id URL exists. The
         * running generation then attaches its stream to this existing row, so
         * navigating away and coming back (immediately, or seconds into the
         * build) reconnects and shows live progress via `activeStreamId`.
         *
         * Previously the first save happened only on stream-finish or the 30s
         * auto-save, leaving a window where navigating away stranded the project
         * with no server row — it "read as gone" on return (DEV-159).
         */
        try {
          const firstUserMessage: UIMessage = {
            id: generateId(),
            role: 'user',
            parts: [{ type: 'text', text: stashContent }],
          };
          await saveChatToServer(
            id,
            [firstUserMessage],
            id,
            descriptionAtom.get(),
            undefined,
            newChatMetadata,
            activeProject,
          );

          if (activeProject) {
            $activeProjectId.set(undefined);
          }
        } catch (err) {
          // Commit failed — do NOT route to a /chat/:id the server doesn't have,
          // which is the very state this fix exists to prevent.
          console.error('Failed to create the new chat before navigating:', err);
          captureException(err instanceof Error ? err : new Error(String(err)), {
            context: 'chat.create.commit_before_navigate',
            chatId: id,
          });
          toast.error("Couldn't start your project. Please try again.");
          setIsStartingChat(false);

          return;
        }

        // Mark started BEFORE navigating — the Home page's useChatHistory
        // effect can re-fire during the transition and will clear chatId
        // unless it sees started=true.
        chatStore.setKey('started', true);
        setChatStarted(true);

        navigateChat(id, navigate, { newChat: true });

        /*
         * Stash the message so the new ChatImpl picks it up after mount.
         * Don't call chatSendMessage here — the old component is about to
         * unmount (route change) so the stream would be aborted immediately.
         */
        $pendingMessage.set(stashContent);

        setInput('');
        clearDraft(draftId);
        setUploadedFiles([]);
        setImageDataList([]);
        setAddToProject([]);
        setPdfDocs([]);
        prevFileCountRef.current = 0;
        resetEnhancer();

        return;
      }

      runAnimation();

      if (error != null) {
        setMessages(messages.slice(0, -1));
      }

      chatStore.setKey('aborted', false);

      // Upload each image twice, in lockstep: the full-res original (assetUrl,
      // referenced in generated code) and a small JPEG preview (previewUrl) for
      // the server-side vision pre-pass. We send hosted URLs only — no base64
      // ever reaches the chat message or Postgres. The build model is text-only,
      // so the server analyzes the preview and strips the image parts before it.
      const assetUrls: string[] = [];
      const fileParts: FileUIPart[] = [];

      /*
       * Images arrive two ways: attached in this component (existing chat) or
       * carried from the landing page's first message via $pendingFiles. Both go
       * through this same upload → [APP_ASSETS] marker → image-file-part path, so
       * the server's vision pre-pass treats them identically. A carried file is a
       * new chat's first upload, so it must claim the not-yet-created chat row.
       */
      const carriedFiles = $pendingFiles.get();
      const imagesToUpload = uploadedFiles.length > 0 ? uploadedFiles : (carriedFiles ?? []);
      const claimOnUpload = uploadedFiles.length === 0 && (carriedFiles?.length ?? 0) > 0;

      if (carriedFiles) {
        $pendingFiles.set(null);
      }

      if (imagesToUpload.length > 0) {
        const uploads = await Promise.all(
          imagesToUpload.map(async (file) => {
            try {
              const preview = await compressImageFile(file);
              const [original, thumb] = await Promise.all([
                apiUploadStorageFile(id, file, undefined, { claim: claimOnUpload }),
                apiUploadStorageFile(id, preview, undefined, { claim: claimOnUpload }),
              ]);

              return { assetUrl: original.url, previewUrl: thumb.url };
            } catch (err) {
              console.error('Failed to upload image to storage:', err);

              return null;
            }
          }),
        );

        for (const u of uploads) {
          if (!u) {
            continue;
          }

          assetUrls.push(u.assetUrl);
          fileParts.push({ type: 'file', mediaType: 'image/jpeg', url: u.previewUrl });
        }
      }

      // Build message text with asset URLs for AI to use in generated code
      let messageText = `[Model: ${model}]\n\n[Provider: ${provider!.name}]\n\n`;

      if (assetUrls.length > 0) {
        messageText += `[APP_ASSETS: ${assetUrls.join(', ')}]\n\n`;
      }

      const selectedElement = $selectedElement.get();

      if (selectedElement && !finalMessageContent.startsWith('[SELECTED_ELEMENT:')) {
        messageText += `${buildSelectedElementMarker(selectedElement)}\n\n`;
        trackSelectionSentToChat(finalMessageContent === DELETE_ELEMENT_INSTRUCTION ? 'delete' : 'prompt');
      }

      messageText += finalMessageContent;

      chatSendMessage({
        text: messageText,
        ...(fileParts.length > 0 ? { files: fileParts } : {}),
      });

      workbenchStore.resetAllFileModifications();

      setInput('');
      clearDraft(draftId);

      setUploadedFiles([]);
      setImageDataList([]);
      setAddToProject([]);
      prevFileCountRef.current = 0;
      clearSelection();

      resetEnhancer();

      textareaRef.current?.blur();
    };

    // Keep ref in sync so auth callback can call latest sendMessage
    sendMessageRef.current = sendMessage;

    /**
     * Handles the change event for the textarea and updates the input state.
     * @param event - The change event from the textarea.
     */
    const onTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleInputChange(event);
    };

    /**
     * Debounced function to cache the prompt in cookies.
     * Caches the trimmed value of the textarea input after a delay to optimize performance.
     */
    const debouncedCachePrompt = useCallback(
      debounce((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDraft(draftId, event.target.value);
      }, 1000),
      [draftId],
    );

    const handleModelChange = (newModel: string) => {
      setModel(newModel);
      Cookies.set('selectedModel', newModel, { expires: 30 });
    };

    const handleProviderChange = (newProvider: ProviderInfo) => {
      setProvider(newProvider);
      Cookies.set('selectedProvider', newProvider.name, { expires: 30 });
    };

    // Wait for provider data before rendering (guard AFTER all hooks)
    if (!provider) {
      return null;
    }

    return (
      <>
        <BaseChat
          ref={animationScope}
          textareaRef={textareaRef}
          input={input}
          showChat={showChat}
          chatStarted={chatStarted}
          sandboxBootFailed={sandboxBootFailed}
          isStreaming={isLoading || fakeLoading || isRestoring}
          onStreamingChange={(streaming) => {
            streamingState.set(streaming);
          }}
          enhancingPrompt={enhancingPrompt}
          promptEnhanced={promptEnhanced}
          enhanceError={enhanceError}
          clearEnhanceError={clearEnhanceError}
          sendMessage={sendMessage}
          model={model}
          setModel={handleModelChange}
          provider={provider}
          setProvider={handleProviderChange}
          providerList={activeProviders}
          handleInputChange={(e) => {
            onTextareaChange(e);
            debouncedCachePrompt(e);
          }}
          handleStop={isRestoring ? undefined : abort}
          canResume={canResume}
          handleResume={handleResume}
          description={description}
          importChat={importChat}
          exportChat={exportChat}
          messages={messages}
          enhancePrompt={() => {
            enhancePrompt(
              input,
              (input) => {
                setInput(input);
                scrollTextArea();
              },
              model,
              provider,
            );
          }}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={handleSetUploadedFiles}
          imageDataList={imageDataList}
          setImageDataList={handleSetImageDataList}
          actionAlert={actionAlert}
          clearAlert={() => workbenchStore.clearAlert()}
          llmErrorAlert={llmErrorAlert}
          clearLlmErrorAlert={clearApiErrorAlert}
          onRetryLlmError={retryLastMessage}
          chatMode={chatMode}
          setChatMode={setChatMode}
          append={chatSendMessage}
          designScheme={designScheme}
          setDesignScheme={(scheme) => setDesignScheme(scheme)}
          projectType={projectType}
          setProjectType={setProjectTypeWithRef}
          isStartingChat={isStartingChat}
          addToProject={addToProject}
          onToggleAddToProject={handleToggleAddToProject}
          onRemoveFile={handleRemoveFile}
          brochure={brochure}
          onBrochureAttach={handleBrochureAttach}
          onBrochureRemove={handleBrochureRemove}
          pdfAttachments={pdfDocs}
          onPdfAttach={handlePdfAttach}
          onPdfAttachRemove={handlePdfAttachRemove}
          checkpoints={checkpoints}
          onRestore={handleRestore}
        />
      </>
    );
  },
);
